import { useEffect, useMemo, useState } from "react";
import api, {
  CreatePropertyRequest,
  CreateCompanyRequest,
  CreateUnitRequest,
  CreateUserPayload,
  CreateAccGroupRequest,
  CreateRequirementTypeRequest,
} from "../../api";
import { CompanyData } from "../../models/company-model";
import { TimeZonesData } from "../../models/time-zones";
import { Roles } from "../../models/role-model";
import {
  AccessGroup,
  AccessPoint,
  DEFAULT_PROPERTY,
  INVITE_ROLES,
  PropertyDetails,
  TOTAL_STEPS,
  WizardUnit,
  WizardUser,
  makeEmptyAccessPoint,
  makeEmptyAccessGroup,
  makeEmptyUser,
  makeInitialUnits,
  unitNameForInviteRole,
  ADMIN_UNIT_NAME,
  resolveGroupRoleId,
  resolveInviteRoleId,
  WEEK_DAY_NAMES,
} from "./types";
import { PropertyAddressMetadata } from "../../models/property-model";
import { uploadDataUrlImage } from "./utils/uploadMedia";
import {
  isValidInviteEmail,
  isValidPhoneDigits,
} from "./utils/userValidation";
import { getApiErrorMessage } from "./utils/apiError";
import type { CreatePropertySuccessSummary } from "./components/ResultDialogs";
import type { PropertySetupLoadedState } from "./utils/loadPropertySetup";
import {
  buildEditSnapshot,
  EditEntitySnapshot,
  isAccessGroupCompleteForSync as isAccessGroupComplete,
  syncPropertyEdit,
} from "./utils/syncPropertyEdit";

export interface UseCreatePropertyWizardOptions {
  editDocumentId?: string;
}

const CREATE_ERROR_TITLES: Record<string, string> = {
  "Verificando correo...": "Error al verificar correos",
  "Guardando compañía...": "Error al guardar la compañía",
  "Subiendo archivos...": "Error al subir archivos",
  "Creando propiedad...": "Error al crear la propiedad",
  "Creando puntos de acceso...": "Error al crear puntos de acceso",
  "Creando unidades...": "Error al crear unidades",
  "Creando grupos de acceso...": "Error al crear grupos de acceso",
  "Creando usuarios...": "Error al crear usuarios",
  "Guardando propiedad...": "Error al guardar la propiedad",
  "Guardando puntos de acceso...": "Error al guardar puntos de acceso",
  "Guardando unidades...": "Error al guardar unidades",
  "Guardando grupos de acceso...": "Error al guardar grupos de acceso",
  "Guardando usuarios...": "Error al guardar usuarios",
};

const createErrorTitleForStage = (stage: string) =>
  CREATE_ERROR_TITLES[stage] ?? "No se pudo crear la propiedad";

const composeAddress = (prop: PropertyDetails) =>
  [
    [prop.street, prop.streetNumber]
      .filter((part) => part?.trim())
      .join(" "),
    prop.colonia,
    prop.city,
    prop.state,
    prop.zip,
  ]
    .filter((part) => part?.trim())
    .join(", ");

const hasAddressData = (prop: PropertyDetails) =>
  [
    prop.street,
    prop.streetNumber,
    prop.colonia,
    prop.city,
    prop.state,
    prop.zip,
  ].some((part) => Boolean(part?.trim()));

/** Only persist map coordinates when the property has at least one address field. */
const resolvePropertyCoordinates = (
  prop: PropertyDetails,
  options?: { clearWhenMissing?: boolean },
) => {
  if (!hasAddressData(prop)) {
    return options?.clearWhenMissing
      ? { latitude: null, longitude: null }
      : {};
  }

  const latitude = Number(prop.lat);
  const longitude = Number(prop.lng);

  return {
    ...(Number.isFinite(latitude) ? { latitude } : {}),
    ...(Number.isFinite(longitude) ? { longitude } : {}),
  };
};

const buildAddressMetadata = (
  prop: PropertyDetails,
): PropertyAddressMetadata => ({
  zip: prop.zip.trim() || undefined,
  state: prop.state.trim() || undefined,
  city: prop.city.trim() || undefined,
  colonia: prop.colonia.trim() || undefined,
  street: prop.street.trim() || undefined,
  streetNumber: prop.streetNumber.trim() || undefined,
  timeZoneId: prop.tz || undefined,
});

const accessPointUsesPropertyCoords = (
  point: AccessPoint,
  propertyLat: string,
  propertyLng: string,
) => {
  const empty = !point.latitud.trim() && !point.longitud.trim();
  const matches =
    point.latitud.trim() === propertyLat.trim() &&
    point.longitud.trim() === propertyLng.trim();
  return empty || matches;
};

const syncAccessPointsToPropertyCoords = (
  points: AccessPoint[],
  oldLat: string,
  oldLng: string,
  newLat: string,
  newLng: string,
) =>
  points.map((point) =>
    accessPointUsesPropertyCoords(point, oldLat, oldLng)
      ? { ...point, latitud: newLat, longitud: newLng }
      : point,
  );

export function useCreatePropertyWizard(
  creatorUserId?: number,
  options?: UseCreatePropertyWizardOptions,
) {
  const [step, setStep] = useState(1);
  const [isEditMode, setIsEditMode] = useState(Boolean(options?.editDocumentId));
  const [editDocumentId, setEditDocumentId] = useState<string | undefined>(
    options?.editDocumentId,
  );
  const [editPropertyId, setEditPropertyId] = useState<number | undefined>();
  const [existingLogoMediaId, setExistingLogoMediaId] = useState<
    number | undefined
  >();
  const [existingLayoutMediaId, setExistingLayoutMediaId] = useState<
    number | undefined
  >();
  const [editSnapshot, setEditSnapshot] = useState<EditEntitySnapshot>({
    accessPointDocumentIds: [],
    gateDocumentIds: [],
    unitDocumentIds: [],
    groupDocumentIds: [],
    userIds: [],
  });
  const [isLoading, setIsLoading] = useState(Boolean(options?.editDocumentId));
  const [loadingMessage, setLoadingMessage] = useState(
    options?.editDocumentId ? "Cargando propiedad..." : "Guardando...",
  );
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successSummary, setSuccessSummary] =
    useState<CreatePropertySuccessSummary | null>(null);
  const [duplicateEmailModalOpen, setDuplicateEmailModalOpen] =
    useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState("");
  const [createErrorModalOpen, setCreateErrorModalOpen] = useState(false);
  const [createErrorTitle, setCreateErrorTitle] = useState("");
  const [createErrorMessage, setCreateErrorMessage] = useState("");
  const [incompleteGroupsModalOpen, setIncompleteGroupsModalOpen] =
    useState(false);

  const showCreateError = (title: string, message: string) => {
    setCreateErrorTitle(title);
    setCreateErrorMessage(message);
    setCreateErrorModalOpen(true);
  };

  const closeCreateErrorModal = () => {
    setCreateErrorModalOpen(false);
    setCreateErrorTitle("");
    setCreateErrorMessage("");
  };

  // --- Etapa 1: Propiedad ----------------------------------------------------
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(
    null,
  );
  const [newCompanyName, setNewCompanyName] = useState("");
  const [prop, setProp] = useState<PropertyDetails>(DEFAULT_PROPERTY);
  const [roles, setRoles] = useState<Roles[]>([]);

  useEffect(() => {
    setProp((prev) => ({ ...DEFAULT_PROPERTY, ...prev }));
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await api.getCompanies();
        const data = (res as unknown as { data: CompanyData[] | CompanyData })
          .data;
        setCompanies(Array.isArray(data) ? data : data ? [data] : []);
      } catch (err) {
        console.error("Error loading companies:", err);
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await api.getRoles();
        setRoles(res.roles ?? []);
      } catch (err) {
        console.error("Error loading roles:", err);
        setRoles([]);
      }
    };
    loadRoles();
  }, []);

  const applyLoadedState = (loaded: PropertySetupLoadedState) => {
    setIsEditMode(true);
    setEditDocumentId(loaded.propertyDocumentId);
    setEditPropertyId(loaded.propertyId);
    setExistingLogoMediaId(loaded.logoMediaId);
    setExistingLayoutMediaId(loaded.layoutMediaId);
    setProp(loaded.prop);
    setSelectedCompany(loaded.selectedCompany);
    setNewCompanyName("");
    setAccessPoints(loaded.accessPoints);
    setSelectedPointId(loaded.accessPoints[0]?.id ?? null);
    setGroups(loaded.groups);
    setSelectedGroupId(loaded.groups[0]?.id ?? null);
    setUnits(loaded.units);
    setNewUnitName("");
    setUsers(loaded.users);
    setNewUser(makeEmptyUser("Administración"));
    setEditSnapshot(
      buildEditSnapshot(
        loaded.accessPoints,
        loaded.units,
        loaded.groups,
        loaded.users,
      ),
    );
    setStep(1);
  };

  useEffect(() => {
    const documentId = options?.editDocumentId;
    if (!documentId) {
      return;
    }

    let cancelled = false;

    const loadProperty = async () => {
      setIsLoading(true);
      setLoadingMessage("Cargando propiedad...");
      try {
        const loaded = await api.getPropertyForSetup(documentId);
        if (!cancelled) {
          applyLoadedState(loaded);
        }
      } catch (error) {
        console.error("Error loading property for edit:", error);
        if (!cancelled) {
          showCreateError(
            "No se pudo cargar la propiedad",
            getApiErrorMessage(
              error,
              "Verifica tu acceso e intenta de nuevo.",
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setLoadingMessage("Guardando...");
        }
      }
    };

    loadProperty();

    return () => {
      cancelled = true;
    };
  }, [options?.editDocumentId]);

  const handleSelectCompany = (company: CompanyData | null) => {
    setSelectedCompany(company);
    if (company) {
      setNewCompanyName("");
    }
  };

  const handleNewCompanyNameChange = (value: string) => {
    setNewCompanyName(value);
    if (value.trim()) {
      setSelectedCompany(null);
    }
  };

  const updateProp = (patch: Partial<PropertyDetails>) =>
    setProp((prev) => {
      const next = { ...DEFAULT_PROPERTY, ...prev, ...patch };
      if (patch.lat !== undefined || patch.lng !== undefined) {
        setAccessPoints((points) =>
          syncAccessPointsToPropertyCoords(
            points,
            prev.lat,
            prev.lng,
            next.lat,
            next.lng,
          ),
        );
      }
      return next;
    });

  const setPropSetting = (key: keyof PropertyDetails["settings"], checked: boolean) =>
    setProp((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: checked },
    }));

  // --- Etapa 2: Puntos de acceso ---------------------------------------------
  const [accessPoints, setAccessPoints] = useState<AccessPoint[]>(() => [
    makeEmptyAccessPoint({
      lat: DEFAULT_PROPERTY.lat,
      lng: DEFAULT_PROPERTY.lng,
    }),
  ]);
  const [selectedPointId, setSelectedPointId] = useState<string | null>(
    accessPoints[0]?.id ?? null,
  );
  const [timeZones, setTimeZones] = useState<TimeZonesData[]>([]);
  const [timeZonesLoading, setTimeZonesLoading] = useState(false);

  useEffect(() => {
    const loadTimeZones = async () => {
      setTimeZonesLoading(true);
      try {
        const res = await api.getTimeZones();
        const data = (
          res as unknown as { data: TimeZonesData[] | TimeZonesData }
        ).data;
        setTimeZones(Array.isArray(data) ? data : data ? [data] : []);
      } catch (err) {
        console.error("Error loading time zones:", err);
        setTimeZones([]);
      } finally {
        setTimeZonesLoading(false);
      }
    };
    loadTimeZones();
  }, []);

  const updateAccessPoint = (id: string, patch: Partial<AccessPoint>) =>
    setAccessPoints((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );

  const addAccessPoint = () => {
    const next = makeEmptyAccessPoint({ lat: prop.lat, lng: prop.lng });
    setAccessPoints((prev) => [...prev, next]);
    setSelectedPointId(next.id);
  };

  // --- Etapa 3: Grupos de acceso ------------------------------------------------
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [deleteAccessPointModalOpen, setDeleteAccessPointModalOpen] =
    useState(false);
  const [pendingDeleteAccessPointId, setPendingDeleteAccessPointId] = useState<
    string | null
  >(null);

  const pendingDeleteAccessPoint = accessPoints.find(
    (point) => point.id === pendingDeleteAccessPointId,
  );
  const pendingDeleteGroupImpact = useMemo(() => {
    if (!pendingDeleteAccessPointId) {
      return { groupsToUpdate: [] as AccessGroup[], groupsToDelete: [] as AccessGroup[] };
    }
    const affected = groups.filter((group) =>
      group.points.includes(pendingDeleteAccessPointId),
    );
    return {
      groupsToUpdate: affected.filter((group) => group.points.length > 1),
      groupsToDelete: affected.filter((group) => group.points.length === 1),
    };
  }, [groups, pendingDeleteAccessPointId]);

  const performRemoveAccessPoint = (id: string) => {
    setAccessPoints((prev) => {
      if (prev.length <= 1) return prev;
      const rest = prev.filter((point) => point.id !== id);
      setSelectedPointId((current) =>
        current === id ? rest[0]?.id ?? null : current,
      );
      return rest;
    });
    setProp((prev) => ({
      ...DEFAULT_PROPERTY,
      ...prev,
      layoutPins: (prev.layoutPins ?? []).filter(
        (pin) => pin.accessPointId !== id,
      ),
    }));
    setGroups((prev) => {
      const cleaned = prev
        .filter((group) => {
          if (!group.points.includes(id)) return true;
          return group.points.length > 1;
        })
        .map((group) =>
          group.points.includes(id)
            ? {
                ...group,
                points: group.points.filter((pointId) => pointId !== id),
              }
            : group,
        );

      setSelectedGroupId((current) =>
        current && !cleaned.some((group) => group.id === current)
          ? cleaned[0]?.id ?? null
          : current,
      );
      return cleaned;
    });
    setUsers((prev) =>
      prev.map((user) =>
        user.points.includes(id)
          ? { ...user, points: user.points.filter((pointId) => pointId !== id) }
          : user,
      ),
    );
  };

  const requestRemoveAccessPoint = (id: string) => {
    if (accessPoints.length <= 1) return;

    const affected = groups.filter((group) => group.points.includes(id));
    if (affected.length === 0) {
      performRemoveAccessPoint(id);
      return;
    }

    setPendingDeleteAccessPointId(id);
    setDeleteAccessPointModalOpen(true);
  };

  const closeDeleteAccessPointModal = () => {
    setDeleteAccessPointModalOpen(false);
    setPendingDeleteAccessPointId(null);
  };

  const confirmRemoveAccessPoint = () => {
    if (!pendingDeleteAccessPointId) return;
    performRemoveAccessPoint(pendingDeleteAccessPointId);
    closeDeleteAccessPointModal();
  };

  const addGroup = () => {
    const group = makeEmptyAccessGroup(`Nuevo grupo ${groups.length + 1}`);
    setGroups((prev) => [...prev, group]);
    setSelectedGroupId(group.id);
  };

  const updateGroup = (id: string, patch: Partial<AccessGroup>) =>
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));

  const removeGroup = (id: string) =>
    setGroups((prev) => {
      const rest = prev.filter((g) => g.id !== id);
      setSelectedGroupId((current) =>
        current === id ? rest[0]?.id ?? null : current,
      );
      return rest;
    });

  // --- Etapa 4: Unidades ------------------------------------------------------
  const [units, setUnits] = useState<WizardUnit[]>(() => makeInitialUnits());
  const [newUnitName, setNewUnitName] = useState("");
  const [deleteUnitModalOpen, setDeleteUnitModalOpen] = useState(false);
  const [pendingDeleteUnitId, setPendingDeleteUnitId] = useState<string | null>(
    null,
  );

  // --- Etapa 5: Usuarios ------------------------------------------------------
  const [users, setUsers] = useState<WizardUser[]>([]);
  const [newUser, setNewUser] = useState<WizardUser>(() =>
    makeEmptyUser("Administración"),
  );

  const pendingDeleteUnit = units.find((unit) => unit.id === pendingDeleteUnitId);
  const pendingDeleteUnitUsers = useMemo(() => {
    if (!pendingDeleteUnit) return [];
    return users.filter(
      (user) =>
        user.role === "Usuario Principal" &&
        user.unit === pendingDeleteUnit.name,
    );
  }, [pendingDeleteUnit, users]);
  const pendingDeleteUnitReassignOptions = useMemo(() => {
    if (!pendingDeleteUnitId) return [];
    return units.filter(
      (unit) => unit.id !== pendingDeleteUnitId && !unit.locked,
    );
  }, [pendingDeleteUnitId, units]);

  const addUnit = () => {
    const name = newUnitName.trim();
    if (!name) return;
    setUnits((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name, locked: false },
    ]);
    setNewUnitName("");
  };

  const renameUnit = (id: string, nextName: string) => {
    const trimmed = nextName.trim();
    if (!trimmed) return false;

    const unit = units.find((item) => item.id === id);
    if (!unit || unit.locked) return false;

    const duplicate = units.some(
      (item) =>
        item.id !== id &&
        item.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) return false;

    const previousName = unit.name;
    if (previousName === trimmed) return true;

    setUnits((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, name: trimmed } : item,
      ),
    );
    setUsers((prev) =>
      prev.map((user) =>
        user.unit === previousName ? { ...user, unit: trimmed } : user,
      ),
    );
    setNewUser((prev) =>
      prev.unit === previousName ? { ...prev, unit: trimmed } : prev,
    );
    return true;
  };

  const performRemoveUnit = (
    id: string,
    decisions?: {
      userId: string;
      action: "delete" | "reassign";
      unitName?: string;
    }[],
  ) => {
    const unit = units.find((item) => item.id === id);
    if (!unit || unit.locked) return;

    setUnits((prev) => prev.filter((item) => item.id !== id));

    if (!decisions || decisions.length === 0) {
      setNewUser((prev) =>
        prev.unit === unit.name ? { ...prev, unit: "" } : prev,
      );
      return;
    }

    const decisionByUserId = new Map(
      decisions.map((decision) => [decision.userId, decision]),
    );
    const firstReassignUnit = decisions.find(
      (decision) => decision.action === "reassign" && decision.unitName,
    )?.unitName;

    setUsers((prev) =>
      prev.flatMap((user) => {
        const decision = decisionByUserId.get(user.id);
        if (!decision) return [user];
        if (decision.action === "delete") return [];
        if (decision.action === "reassign" && decision.unitName) {
          return [{ ...user, unit: decision.unitName }];
        }
        return [];
      }),
    );

    setNewUser((prev) => {
      if (prev.unit !== unit.name) return prev;
      return { ...prev, unit: firstReassignUnit ?? "" };
    });
  };

  const requestRemoveUnit = (id: string) => {
    const unit = units.find((item) => item.id === id);
    if (!unit || unit.locked) return;

    const linkedPrincipals = users.filter(
      (user) =>
        user.role === "Usuario Principal" && user.unit === unit.name,
    );

    if (linkedPrincipals.length === 0) {
      performRemoveUnit(id);
      return;
    }

    setPendingDeleteUnitId(id);
    setDeleteUnitModalOpen(true);
  };

  const closeDeleteUnitModal = () => {
    setDeleteUnitModalOpen(false);
    setPendingDeleteUnitId(null);
  };

  const confirmRemoveUnit = (
    decisions: {
      userId: string;
      action: "delete" | "reassign";
      unitName?: string;
    }[],
  ) => {
    if (!pendingDeleteUnitId) return;
    performRemoveUnit(pendingDeleteUnitId, decisions);
    closeDeleteUnitModal();
  };

  const updateNewUser = (patch: Partial<WizardUser>) =>
    setNewUser((prev) => {
      const next = { ...prev, ...patch };
      if (patch.role !== undefined) {
        next.unit = unitNameForInviteRole(patch.role, prev.unit);
        if (!INVITE_ROLES.find((r) => r.key === patch.role)?.needsPoints) {
          next.points = [];
        }
      }
      return next;
    });

  const addUser = () => {
    const roleMeta = INVITE_ROLES.find((r) => r.key === newUser.role);
    if (!newUser.name.trim()) return;
    if (!isValidInviteEmail(newUser.email)) return;
    if (!isValidPhoneDigits(newUser.phoneNumber)) return;
    if (roleMeta?.needsUnit && !newUser.unit.trim()) return;
    if (roleMeta?.needsPoints && newUser.points.length === 0) return;

    const unit =
      newUser.role === "Administración"
        ? ADMIN_UNIT_NAME
        : newUser.unit;

    setUsers((prev) => [
      ...prev,
      { ...newUser, id: crypto.randomUUID(), unit },
    ]);
    setNewUser(makeEmptyUser(newUser.role));
  };

  const addUserToUnit = (
    unitId: string,
    data: {
      name: string;
      email: string;
      phoneCountryCode: string;
      phoneNumber: string;
    },
  ) => {
    const unit = units.find((item) => item.id === unitId);
    if (!unit) return false;
    if (!data.name.trim()) return false;
    if (!isValidInviteEmail(data.email)) return false;
    if (!isValidPhoneDigits(data.phoneNumber)) return false;

    const role = unit.locked ? "Administración" : "Usuario Principal";
    setUsers((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: data.name.trim(),
        email: data.email.trim(),
        phoneCountryCode: data.phoneCountryCode || "+52",
        phoneNumber: data.phoneNumber,
        role,
        unit: unit.name,
        points: [],
        locked: false,
      },
    ]);
    return true;
  };

  const removeUser = (id: string) =>
    setUsers((prev) => prev.filter((u) => !(u.id === id && !u.locked)));

  const hasAdminUser = useMemo(
    () => users.some((u) => u.role === "Administración"),
    [users],
  );

  // Checkmarks in the sidebar follow minimum data per step, not just
  // whether the user has navigated past that step.
  const completedSteps = useMemo(
    () => ({
      1:
        prop.name.trim().length > 0 &&
        Boolean(prop.type) &&
        Boolean(prop.tz) &&
        Boolean(selectedCompany || newCompanyName.trim()),
      2: accessPoints.some((point) => point.nombre.trim().length > 0),
      3: groups.length === 0 || groups.every(isAccessGroupComplete),
      4: units.some((unit) => unit.locked || unit.name.trim().length > 0),
      5: hasAdminUser,
    }),
    [
      prop.name,
      prop.type,
      prop.tz,
      selectedCompany,
      newCompanyName,
      accessPoints,
      groups,
      units,
      hasAdminUser,
    ],
  );

  // --- Navegación / gating (misma lógica que el diseño) -----------------------
  const createNextDisabled =
    step === 1
      ? !prop.name.trim() ||
        !prop.type ||
        !prop.tz ||
        !(selectedCompany || newCompanyName.trim())
      : step === 2
        ? !accessPoints.some((point) => point.nombre.trim().length > 0)
        : step === 3
          ? groups.some((group) => !isAccessGroupComplete(group))
          : step === 5
            ? !hasAdminUser
            : false;

  const editSaveDisabled =
    !prop.name.trim() ||
    !prop.type ||
    !prop.tz ||
    !(selectedCompany || newCompanyName.trim()) ||
    !accessPoints.some((point) => point.nombre.trim().length > 0) ||
    !hasAdminUser;

  const nextDisabled = isEditMode ? editSaveDisabled : createNextDisabled;

  const incompleteAccessGroups = useMemo(
    () => groups.filter((group) => !isAccessGroupComplete(group)),
    [groups],
  );

  const back = () => setStep((s) => Math.max(1, s - 1));

  const next = () => {
    if (isEditMode) {
      if (incompleteAccessGroups.length > 0) {
        setIncompleteGroupsModalOpen(true);
        return;
      }
      submitWizard();
      return;
    }

    if (step === TOTAL_STEPS) {
      if (incompleteAccessGroups.length > 0) {
        setIncompleteGroupsModalOpen(true);
        return;
      }
      submitWizard();
      return;
    }
    setStep((s) => s + 1);
  };

  const closeIncompleteGroupsModal = () => {
    setIncompleteGroupsModalOpen(false);
  };

  const continueDespiteIncompleteGroups = () => {
    setIncompleteGroupsModalOpen(false);
    submitWizard();
  };

  const resetWizard = () => {
    const initialAccessPoint = makeEmptyAccessPoint({
      lat: DEFAULT_PROPERTY.lat,
      lng: DEFAULT_PROPERTY.lng,
    });

    setIsEditMode(false);
    setEditDocumentId(undefined);
    setEditPropertyId(undefined);
    setExistingLogoMediaId(undefined);
    setExistingLayoutMediaId(undefined);
    setEditSnapshot({
      accessPointDocumentIds: [],
      gateDocumentIds: [],
      unitDocumentIds: [],
      groupDocumentIds: [],
      userIds: [],
    });
    setStep(1);
    setSuccessModalOpen(false);
    setSuccessSummary(null);
    setDuplicateEmailModalOpen(false);
    setDuplicateEmail("");
    setCreateErrorModalOpen(false);
    setCreateErrorTitle("");
    setCreateErrorMessage("");
    setIncompleteGroupsModalOpen(false);
    setDeleteAccessPointModalOpen(false);
    setPendingDeleteAccessPointId(null);
    setDeleteUnitModalOpen(false);
    setPendingDeleteUnitId(null);
    setSelectedCompany(null);
    setNewCompanyName("");
    setProp(DEFAULT_PROPERTY);
    setAccessPoints([initialAccessPoint]);
    setSelectedPointId(initialAccessPoint.id);
    setGroups([]);
    setSelectedGroupId(null);
    setUnits(makeInitialUnits());
    setNewUnitName("");
    setUsers([]);
    setNewUser(makeEmptyUser("Administración"));
    setIsLoading(false);
    setLoadingMessage("Guardando...");
  };

  const closeSuccessModal = () => {
    setSuccessModalOpen(false);
    setSuccessSummary(null);
  };

  const submitPropertyUpdate = async () => {
    if (!editDocumentId || !editPropertyId) {
      return;
    }

    let stage = "Guardando cambios...";
    const setStage = (message: string) => {
      stage = message;
      setLoadingMessage(message);
    };

    try {
      setIsLoading(true);
      setStage("Guardando compañía...");

      let companyId: number | undefined;
      if (selectedCompany) {
        companyId = selectedCompany.id;
      } else if (newCompanyName.trim()) {
        const responseCompany = await api.createCompany({
          data: { name: newCompanyName.trim() },
        });
        companyId = responseCompany?.data?.id;
      }

      setStage("Subiendo archivos...");
      const propertySlug = (prop.name || "property").replace(/\s+/g, "-").toLowerCase();
      const [logoMediaId, layoutMediaId] = await Promise.all([
        uploadDataUrlImage(prop.logo, `${propertySlug}-logo`),
        uploadDataUrlImage(prop.layoutImage, `${propertySlug}-layout`),
      ]);
      const finalLogoId = logoMediaId ?? existingLogoMediaId;
      const finalLayoutId = layoutMediaId ?? existingLayoutMediaId;

      setStage("Guardando propiedad...");
      await api.updateProperty(editDocumentId, {
        data: {
          name: prop.name || "",
          address: composeAddress(prop) || "",
          address_metadata: buildAddressMetadata(prop),
          ...resolvePropertyCoordinates(prop, { clearWhenMissing: true }),
          type:
            (prop.type as "Residential" | "Industrial" | "Corporate") || "",
          settings: prop.settings,
          ...(companyId ? { company: companyId } : {}),
          ...(finalLogoId ? { image: finalLogoId } : {}),
          ...(finalLayoutId ? { layout: finalLayoutId } : {}),
        },
      });

      await syncPropertyEdit({
        propertyId: editPropertyId,
        companyId,
        prop,
        accessPoints,
        units,
        groups,
        users,
        roles,
        snapshot: editSnapshot,
        setStage,
      });

      setStage("Actualizando datos...");
      const reloaded = await api.getPropertyForSetup(editDocumentId);
      applyLoadedState(reloaded);

      setSuccessSummary({
        propertyName: reloaded.prop.name,
        pointsCount: reloaded.accessPoints.length,
        doorsCount: reloaded.accessPoints.reduce(
          (acc, point) => acc + point.gates.length,
          0,
        ),
        groupsCount: reloaded.groups.filter(isAccessGroupComplete).length,
        unitsCount: reloaded.units.length,
        usersCount: reloaded.users.length,
      });
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Error updating property:", error);
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        (error as { code?: string }).code === "DUPLICATE_EMAIL"
      ) {
        const message =
          error instanceof Error ? error.message : "Correo duplicado";
        const emailMatch = message.match(/correo\s+(.+)$/i);
        setDuplicateEmail(emailMatch?.[1] ?? "");
        setDuplicateEmailModalOpen(true);
        return;
      }
      showCreateError(
        createErrorTitleForStage(stage),
        getApiErrorMessage(
          error,
          "Ocurrió un error al guardar los cambios. Intenta de nuevo.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // --- Guardar (misma orquestación/endpoints que el App.tsx original) --------
  const submitWizard = async () => {
    if (isEditMode && editDocumentId) {
      await submitPropertyUpdate();
      return;
    }

    let stage = "Verificando correo...";

    const setStage = (message: string) => {
      stage = message;
      setLoadingMessage(message);
    };

    try {
      setIsLoading(true);
      setStage("Verificando correo...");

      const usersToCreate = users.filter((u) => u.name.trim());
      for (const user of usersToCreate) {
        if (!user.email.trim()) continue;
        const existing = await api.getUsersByEmail(user.email);
        if (existing.length > 0) {
          setDuplicateEmail(user.email);
          setDuplicateEmailModalOpen(true);
          return;
        }
      }

      setStage("Guardando compañía...");
      let companyId: number | undefined;
      if (selectedCompany) {
        companyId = selectedCompany.id;
      } else {
        const createCompanyRequest: CreateCompanyRequest = {
          data: { name: newCompanyName || "" },
        };
        const responseCompany = await api.createCompany(createCompanyRequest);
        companyId = responseCompany?.data?.id;
      }

      if (!companyId) {
        showCreateError(
          createErrorTitleForStage(stage),
          "No se obtuvo un identificador de compañía. Verifica el nombre e intenta de nuevo.",
        );
        return;
      }

      setStage("Subiendo archivos...");
      const propertySlug = (prop.name || "property").replace(/\s+/g, "-").toLowerCase();
      const [logoMediaId, layoutMediaId] = await Promise.all([
        uploadDataUrlImage(prop.logo, `${propertySlug}-logo`),
        uploadDataUrlImage(prop.layoutImage, `${propertySlug}-layout`),
      ]);

      setStage("Creando propiedad...");
      const createPropertyRequest: CreatePropertyRequest = {
        data: {
          name: prop.name || "",
          address: composeAddress(prop) || "",
          address_metadata: buildAddressMetadata(prop),
          ...resolvePropertyCoordinates(prop),
          type:
            (prop.type as "Residential" | "Industrial" | "Corporate") || "",
          settings: prop.settings,
          company: companyId,
          passback_activated_by_default: true,
          license: 1,
          ...(logoMediaId ? { image: logoMediaId } : {}),
          ...(layoutMediaId ? { layout: layoutMediaId } : {}),
        },
      };
      const responseProperty = await api.createProperty(createPropertyRequest);
      const propertyId = responseProperty?.data?.id;

      if (!propertyId) {
        showCreateError(
          createErrorTitleForStage(stage),
          "La propiedad no se creó correctamente. Intenta de nuevo.",
        );
        return;
      }

      if (creatorUserId) {
        await api.createConfigProperty({
          data: {
            users: [creatorUserId],
            property: Number(propertyId),
          },
        });
      }

      setStage("Creando puntos de acceso...");
      const accessPointIdByLocalId: Record<string, number> = {};
      for (const point of accessPoints) {
        const layoutPin = prop.layoutPins.find(
          (pin) => pin.accessPointId === point.id,
        );
        const responseAccessPoint = await api.createAccessPoint({
          data: {
            name: point.nombre,
            vanue_name: point.calleOAvenida,
            longitude: Number(point.longitud) || undefined,
            latitude: Number(point.latitud) || undefined,
            property: propertyId,
            time_zone_custom: Number(prop.tz) || undefined,
            layout_coordinates: layoutPin
              ? {
                  xPercent: layoutPin.xPercent,
                  yPercent: layoutPin.yPercent,
                }
              : undefined,
          },
        });
        const apId = Number(responseAccessPoint?.data?.id);
        accessPointIdByLocalId[point.id] = apId;
        for (const gate of point.gates) {
          await api.createGate({
            data: {
              name: gate.name,
              type: gate.type === "Entrada" ? "entrance" : "exit",
              access_point: apId,
            },
          });
        }
      }
      const accessPointIds = Object.values(accessPointIdByLocalId);

      setStage("Creando unidades...");
      const unitIdByLocalId: Record<string, number> = {};
      for (const unit of units) {
        const createUnitRequest: CreateUnitRequest = {
          data: {
            name: unit.locked ? `Admin ${prop.name}` : unit.name,
            property: Number(propertyId),
            access_points: accessPointIds,
            is_admin: unit.locked ? true : false,
          },
        };
        const responseUnit = await api.createUnit(createUnitRequest);
        unitIdByLocalId[unit.id] = Number(responseUnit?.data?.id);
      }
      const adminUnit = units.find((u) => u.locked);
      const adminUnitId = adminUnit ? unitIdByLocalId[adminUnit.id] : undefined;

      setStage("Creando grupos de acceso...");
      // Etapa 3 (Grupos de acceso): now that the real /api/access-groups and
      // /api/requirement-types contracts are known, actually persist them.
      // Our UI captures one schedule/photos/days set per group (not per
      // access point), so the same values are repeated into a
      // `configuration` entry for every selected point — that's a faithful
      // mapping of what the step's UI collects, not an invented feature.
      // A few real backend fields have no UI control yet (antipassback,
      // printable_pass, per-point exit range vs the single `exit_restriction`
      // value the backend expects) — defaulted conservatively below.
      for (const group of groups.filter(isAccessGroupComplete)) {
        const groupAccessPointIds = group.points
          .map((localId) => accessPointIdByLocalId[localId])
          .filter((id): id is number => Number.isFinite(id));
        const activeDayNames = WEEK_DAY_NAMES.filter((_, i) => group.days[i]);
        const activePhotoKeys = Object.entries(group.photos)
          .filter(([, on]) => on)
          .map(([key]) => key);
        const configuration = groupAccessPointIds.map((apId) => ({
          id: apId,
          week_days: activeDayNames,
          start_time: `${group.from}:00`,
          end_time: `${group.to}:00`,
          exit_restriction: group.exitRestricted
            ? `${group.exitFrom}:00`
            : undefined,
          antipassback: 0,
          description: group.name,
          name: group.name,
          register: true,
          photos: activePhotoKeys,
          printable_pass: false,
        }));

        const createAccGroupRequest: CreateAccGroupRequest = {
          data: {
            name: group.name,
            company: companyId ?? null,
            properties: propertyId ? [Number(propertyId)] : [],
            access_points: groupAccessPointIds,
            role_permissions_users: group.roles
              .map((role) => resolveGroupRoleId(roles, role))
              .filter((id): id is number => id != null),
            configuration,
            banned_users: [],
            unit_access_group: false,
          },
        };
        const responseGroup = await api.createAccessGroup(createAccGroupRequest);
        const groupId = responseGroup?.data?.id;
        if (!groupId) continue;

        if (prop.settings.has_requirements_permissions) {
          for (const field of group.fields) {
            if (!field.label.trim()) continue;
            const dataType = field.type === "selector" ? "selector" : "string";
            const createRequirementTypeRequest: CreateRequirementTypeRequest = {
              data: {
                name: field.label,
                description: field.label,
                data_type: dataType,
                required: field.required,
                access_key: false,
                transaction: true,
                access_groups: [groupId],
                url: "",
                ...(dataType === "selector"
                  ? {
                      selector_data: field.options
                        .split(",")
                        .map((opt) => opt.trim())
                        .filter(Boolean),
                    }
                  : {}),
              },
            };
            await api.createRequirementType(createRequirementTypeRequest);
          }
        }
      }

      setStage("Creando usuarios...");
      for (const user of usersToCreate) {
        const roleMeta = INVITE_ROLES.find((r) => r.key === user.role);
        const targetUnit = units.find((u) => u.name === user.unit);
        const unitId =
          user.role === "Administración"
            ? adminUnitId
            : roleMeta?.needsUnit && targetUnit
              ? unitIdByLocalId[targetUnit.id]
              : undefined;

        const createUserRequest: CreateUserPayload = {
          name: user.name,
          username: user.name,
          email: user.email,
          phone: `${user.phoneCountryCode}${user.phoneNumber}`,
          unit: unitId,
          property: Number(propertyId),
          company: Number(companyId),
          access_points: roleMeta?.needsPoints
            ? user.points
                .map((localId) => accessPointIdByLocalId[localId])
                .filter((id): id is number => Number.isFinite(id))
            : accessPointIds,
          blocked: false,
          license: 1,
          password: "temporal",
        };
        const responseUser = await api.createUser(createUserRequest);
        const createdUserId = responseUser?.user?.id;
        const confirmationToken = responseUser?.confirmation_token
          ? String(responseUser.confirmation_token)
          : "";

        if (!createdUserId) {
          throw new Error(
            `No se pudo crear el usuario "${user.name || user.email}".`,
          );
        }

        const roleId = resolveInviteRoleId(roles, user.role);
        if (roleId == null) {
          throw new Error(
            `No se pudo asignar el rol "${user.role}" a "${user.name || user.email}".`,
          );
        }
        await api.addUserRole(createdUserId, roleId);

        if (!confirmationToken) {
          throw new Error(
            `El usuario "${user.email}" se creó sin token de confirmación; no se pudo enviar el correo.`,
          );
        }
        await api.sendConfirmationEmail({
          email: user.email,
          confirmation_token: confirmationToken,
        });
      }

      setSuccessSummary({
        propertyName: prop.name,
        pointsCount: accessPoints.length,
        doorsCount: accessPoints.reduce((acc, point) => acc + point.gates.length, 0),
        groupsCount: groups.filter(isAccessGroupComplete).length,
        unitsCount: units.length,
        usersCount: usersToCreate.length,
      });
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Error creating property:", error);
      showCreateError(
        createErrorTitleForStage(stage),
        getApiErrorMessage(
          error,
          "Ocurrió un error inesperado. Intenta de nuevo o contacta soporte.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    step,
    setStep,
    isEditMode,
    editDocumentId,
    successModalOpen,
    successSummary,
    closeSuccessModal,
    resetWizard,
    isLoading,
    loadingMessage,
    duplicateEmailModalOpen,
    duplicateEmail,
    closeDuplicateEmailModal: () => setDuplicateEmailModalOpen(false),
    createErrorModalOpen,
    createErrorTitle,
    createErrorMessage,
    closeCreateErrorModal,
    incompleteGroupsModalOpen,
    incompleteAccessGroupsCount: incompleteAccessGroups.length,
    closeIncompleteGroupsModal,
    continueDespiteIncompleteGroups,
    deleteAccessPointModalOpen,
    pendingDeleteAccessPointName: pendingDeleteAccessPoint?.nombre ?? "",
    pendingDeleteGroupsToUpdate: pendingDeleteGroupImpact.groupsToUpdate.map(
      (group) => group.name,
    ),
    pendingDeleteGroupsToDelete: pendingDeleteGroupImpact.groupsToDelete.map(
      (group) => group.name,
    ),
    closeDeleteAccessPointModal,
    confirmRemoveAccessPoint,
    deleteUnitModalOpen,
    pendingDeleteUnitName: pendingDeleteUnit?.name ?? "",
    pendingDeleteUnitUsers: pendingDeleteUnitUsers.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
    })),
    pendingDeleteUnitReassignOptions: pendingDeleteUnitReassignOptions.map(
      (unit) => ({ id: unit.id, name: unit.name }),
    ),
    closeDeleteUnitModal,
    confirmRemoveUnit,

    companies,
    companiesLoading,
    selectedCompany,
    setSelectedCompany: handleSelectCompany,
    newCompanyName,
    setNewCompanyName: handleNewCompanyNameChange,

    prop,
    updateProp,
    setPropSetting,

    accessPoints,
    selectedPointId,
    setSelectedPointId,
    updateAccessPoint,
    addAccessPoint,
    removeAccessPoint: requestRemoveAccessPoint,
    timeZones,
    timeZonesLoading,

    groups,
    selectedGroupId,
    setSelectedGroupId,
    addGroup,
    updateGroup,
    removeGroup,

    units,
    newUnitName,
    setNewUnitName,
    addUnit,
    renameUnit,
    removeUnit: requestRemoveUnit,

    users,
    newUser,
    updateNewUser,
    addUser,
    addUserToUnit,
    removeUser,
    hasAdminUser,

    nextDisabled,
    completedSteps,
    next,
    back,
  };
}

export type PropertySetupWizardState = ReturnType<typeof useCreatePropertyWizard>;
